import { useEffect, useCallback, useRef } from "react";
import { useRouter } from "next/router";
import { saveDeviceToken } from "@/services/axios/protectedApi";
import { navigateFromNotification } from "@/util/navigateFromNotification";
import {
  readDeviceLocationMessage,
  type DeviceLocationStatus,
} from "@/util/deviceLocationBridge";

type Options = {
  onDeviceLocationStatus?: (enabled: DeviceLocationStatus) => void;
  /**
   * Optional override for NOTIFICATION_OPENED navigation.
   * Defaults to router.push(payload.data.url).
   */
  onNotificationOpen?: (remoteMessage: any) => void;

  /**
   * Called when RN reports that the user has denied notification permission.
   * Fires on every app open until the user grants permission from device Settings.
   * Use this to show a reminder modal prompting them to enable notifications.
   */
  onRnNotificationPermissionDenied?: () => void;
};

export function useMobileBridge(options: Options = {}) {
  const emailRef = useRef<string | null>(null);

  const {
    onNotificationOpen,
    onRnNotificationPermissionDenied,
    onDeviceLocationStatus,
  } = options;
  const router = useRouter();

  /** Send a typed message to React Native */
  const postToNative = useCallback((type: string, payload?: unknown) => {
    window.ReactNativeWebView?.postMessage(JSON.stringify({ type, payload }));
  }, []);

  /**
   * Call this after your auth state resolves.
   * Place it in your auth provider/hook right after you determine login state.
   */
  const sendAuthState = useCallback(
    (loggedIn: boolean, email?: string) => {
      emailRef.current = email || null;
      postToNative("USER_AUTH_STATE", { loggedIn, email });
    },
    [postToNative]
  );

  useEffect(() => {
    const handleMessage = async (event: Event) => {
      const { data: rawData } = event as MessageEvent;
      const locationStatus = readDeviceLocationMessage(rawData);
      if (locationStatus) {
        onDeviceLocationStatus?.(locationStatus.enabled);
        return;
      }
      let data: { type: string; payload?: any };
      try {
        data = JSON.parse(rawData);
      } catch {
        return; // not a bridge message
      }

      switch (data?.type) {
        case "FCM_TOKEN":
          // Save token via protectedApi → upsert in MongoDB
          // ACK is sent only on success; RN will retry on next login if we don't ACK
          try {
            await saveDeviceToken({
              token: data.payload?.token,
              email: emailRef.current || data.payload?.email,
            });
            postToNative("FCM_TOKEN_ACK");
          } catch (err) {
            console.error("[MobileBridge] Failed to save FCM token:", err);
          }
          break;
        case "NOTIFICATION_OPENED":
          if (onNotificationOpen) {
            onNotificationOpen(data.payload);
          } else {
            // First check if the payload explicitly provides a direct web URL
            const url = data.payload?.data?.url;
            if (url) {
              void router.push(url);
            } else if (data.payload?.data) {
              // Otherwise, use the shared deep-linking logic
              // Inject the body as text since the util expects it
              const payloadData = {
                ...data.payload.data,
                // text: data.payload.data.text || data.payload.notification?.body,
              };
              void navigateFromNotification(payloadData, router);
            }
          }
          break;

        case "NOTIFICATION_PERMISSION_DENIED":
          onRnNotificationPermissionDenied?.();
          break;
      }
    };

    // Both listeners needed: iOS uses window, Android uses document
    window.addEventListener("message", handleMessage);
    document.addEventListener("message", handleMessage);

    // Signal to RN that Next.js is ready to receive messages
    postToNative("APP_READY");

    return () => {
      window.removeEventListener("message", handleMessage);
      document.removeEventListener("message", handleMessage);
    };
  }, [
    onNotificationOpen,
    onRnNotificationPermissionDenied,
    onDeviceLocationStatus,
    postToNative,
    router,
  ]);

  const openAppSettings = useCallback(() => {
    postToNative("OPEN_APP_SETTINGS");
  }, [postToNative]);

  const openDeviceLocationSettings = useCallback(() => {
    postToNative("OPEN_DEVICE_LOCATION_SETTINGS");
  }, [postToNative]);

  const setAppBadgeCount = useCallback(
    (count: number) => {
      postToNative("SET_BADGE_COUNT", { count });
    },
    [postToNative]
  );

  return {
    postToNative,
    sendAuthState,
    openAppSettings,
    openDeviceLocationSettings,
    setAppBadgeCount,
  };
}
